import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { colors } from '@sweep-io/sweep-design';
import uniq from 'lodash/uniq';
import {
  OpenAiResponse,
  OpenAiDescription,
  SalesforceItem,
  SWEEP_DEDUPE,
  SWEEP_AUTOMATION,
  SWEEP_MATCHING,
  SWEEP_ASSIGNMENT,
  SWEEP_SCHEDULED_ASSIGNMENT,
  SWEEP_ALERT,
  ROLLUP,
} from '../../parser/ParserTypes';
import { readableUUID } from '../../../navigation-utils/readableUUID';
import { getDataFromRecordTypeIdentifier, getDataFromStepId } from '../pills/pillsUtils';
import { FIELD, APEX_CLASS, APEX_TRIGGER, RECORD_TYPE, OBJECT } from '@server/parser';

export const SectionBox = styled(Box)(() => ({
  padding: '0 0 16px 0',
  borderBottom: `1px solid ${colors.grey[120]}`,
  width: '100%',
}));

export const HEADER_TOP_ENDORNMENT = '28px';

export const SWEEP_ELEMENTS = [
  SWEEP_ALERT,
  SWEEP_DEDUPE,
  SWEEP_AUTOMATION,
  SWEEP_MATCHING,
  SWEEP_ASSIGNMENT,
  SWEEP_SCHEDULED_ASSIGNMENT,
  ROLLUP,
];

export const isNonSweepItemMapEmpty = (items?: Record<string, unknown[]>) =>
  !items ||
  Object.entries(items).every(
    ([key, arr]) => SWEEP_ELEMENTS.includes(key) || arr.length === 0,
  );

export const TYPE_TO_SWEEP_ELEMENT_TYPE = {
  automations: SWEEP_AUTOMATION,
  alerts: SWEEP_ALERT,
  assignments: SWEEP_ASSIGNMENT,
  scheduledAssignments: SWEEP_SCHEDULED_ASSIGNMENT,
  dedup: SWEEP_DEDUPE,
  matching: SWEEP_MATCHING,
  rollups: ROLLUP,
};

export const excludedFromDetails = [
  APEX_CLASS,
  APEX_TRIGGER,
  OBJECT,
  RECORD_TYPE,
  ...SWEEP_ELEMENTS,
];

export const isFieldType = (itemType: string) => itemType === FIELD || itemType === ROLLUP;

export const formatOpenAiResponse = (res: OpenAiResponse): OpenAiDescription => {
  const isError = Boolean(res.error);
  return {
    type: isError ? 'error' : 'data',
    text: (res.data as string) || 'Could not load content at this time',
  };
};

export const getChips = (
  configurationItem: SalesforceItem,
  stepNamesUsedInCanvas: string[],
  saltoObjectTypes: Pick<ObjectTypeName, 'objectType' | 'label'>[],
) => {
  const { objectApiNames, stagesNames, usedByRecordTypes } = configurationItem ?? {};

  const objectLabels = objectApiNames?.map(
    (name) => saltoObjectTypes?.find((type) => type.objectType === name)?.label ?? name,
  );

  const stageNamesFromId = uniq(
    stagesNames
      ?.filter(
        (stageName) =>
          stageName &&
          stepNamesUsedInCanvas.some(
            (stepName) =>
              getDataFromStepId(stepName).stepFullName ===
              getDataFromStepId(stageName).stepFullName,
          ),
      )
      ?.map((name) => getDataFromStepId(name).stepFullName),
  );

  const stageNameChips = stageNamesFromId?.length ? stageNamesFromId : [];
  const objectChips = objectLabels && objectLabels?.length > 1 ? objectLabels : [];
  const RTchips = usedByRecordTypes?.length
    ? uniq(
        usedByRecordTypes
          ?.filter((rt) => !!rt)
          .map((rt) => getDataFromRecordTypeIdentifier(rt).recordTypeName),
      )
    : [];
  const chips =
    stageNameChips.length || RTchips.length ? [...stageNameChips, ...RTchips] : [...objectChips];

  return { chips, RTchips };
};

export const buildConfigurationItemId = (item: {
  parentType: string;
  name: string;
  id?: string;
}) => {
  return (
    item.parentType +
    '|' +
    item.name +
    (item.id && readableUUID(item.id) ? '|' + readableUUID(item.id) : '')
  );
};
